import React, { useEffect, ReactNode } from "react";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { useAuthContext } from "./AuthContext";
import { POST_HOG_OPTIONS } from "../constants";

/**
 * Props interface for the TelemetryProvider component
 */
interface TelemetryProviderProps {
  /** Child components that will have access to telemetry context */
  children: ReactNode;
}

/**
 * Internal component that handles PostHog user identification based on auth state
 * This component must be rendered inside both PostHogProvider and AuthProvider
 */
const PostHogAuthSync: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthContext();

  // Use the usePostHog hook to get the PostHog instance
  const posthog = usePostHog();

  /**
   * Sync PostHog user identification with authentication state
   * This effect runs whenever the user or authentication state changes
   * Ensures that the user identification is always updated in PostHog
   */
  useEffect(() => {
    if (isAuthenticated && user) {
      // User is authenticated, identify them in PostHog
      posthog.identify(user._id);
    } else {
      // User is not authenticated or logged out, reset PostHog session
      posthog.reset();
    }
  }, [isAuthenticated, user, posthog]);

  return <>{children}</>;
};

/**
 * TelemetryProvider Component
 *
 * Provides comprehensive telemetry context to the application by combining:
 * 1. PostHog initialization and provider setup
 * 2. Automatic user identification based on authentication state
 *
 * This component should wrap the entire app and be placed inside AuthProvider
 * to ensure it has access to authentication context.
 */
export const TelemetryProvider: React.FC<TelemetryProviderProps> = ({
  children,
}) => {
  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY as string}
      options={POST_HOG_OPTIONS}
    >
      <PostHogAuthSync>{children}</PostHogAuthSync>
    </PostHogProvider>
  );
};
